import React, { useEffect, useState } from "react";
import { getAccessToken } from "../../services/authApi";
import { getUserDetails, getTpoSettingsAllBasicInfo } from "../../services/userApi";
import Loading from "../layout/Loading";
import { hasPermission } from "../../utils/authUtils";
import { PERMISSIONS } from "../../constants/permissions";
import { getLoggedInUserPersonas } from "../../utils/userPersonas";

const WHOLESALE_CHANNEL = "Broker";
const CORRESPONDENT_CHANNEL = "Correspondent";
const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const QUICK_PRICING_DOMAIN = process.env.REACT_APP_QUICK_PRICING_DOMAIN || '';

// Channel-keyed sections; each section renders as one card on the page.
const PRICER_SECTIONS = [
    {
        key: "wholesale",
        title: "Wholesale Quick Pricer",
        links: [
            {
                label: "Quick Pricer",
                url: `${QUICK_PRICING_DOMAIN}/QuickPrice`,
            },
        ],
    },
    {
        key: "correspondent",
        title: "Correspondent Quick Pricer",
        links: [
            {
                key: "Delegated",
                label: "Correspondent Delegated Quick Pricer",
                url: `${QUICK_PRICING_DOMAIN}/CorrQuickPrice?del=1`,
            },
            {
                key: "NonDelegated",
                label: "Correspondent Non-Delegated Quick Pricer",
                url: `${QUICK_PRICING_DOMAIN}/CorrQuickPrice`,
            },
        ],
    },
];

const openLink = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
};

const PricerSection = ({ title, links }) => (
    <div
        style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: 520,
            paddingTop: "32px",
        }}
    >
        <h2
            style={{
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "2.5rem",
                margin: 0,
                letterSpacing: 0.5,
                lineHeight: 1.1,
            }}
        >
            {title}
        </h2>
        <div
            style={{
                background: "#fff",
                borderRadius: "24px",
                boxShadow: "0 2px 16px #0001",
                padding: "32px",
                minWidth: "520px",
                marginTop: 16,
                height: "160px",
                boxSizing: "border-box",
                overflow: "auto",
            }}
        >
            <ul
                style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    color: "#23272f",
                }}
            >
                {links.map((link) => (
                    <li
                        key={link.url}
                        style={{
                            margin: "12px 0",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        <img
                            src="/content/Images/Quick_Price.svg"
                            alt="link"
                            style={{
                                marginRight: "12px",
                                width: 26,
                                height: 26,
                                display: "inline-block",
                                verticalAlign: "middle",
                            }}
                        />
                        <a
                            href={link.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                                e.preventDefault();
                                openLink(link.url);
                            }}
                            className="matrix-link"
                            style={{ fontWeight: 600 }}
                        >
                            {link.label}
                        </a>
                    </li>
                ))}
            </ul>
        </div>
    </div>
);

const QuickPricer = () => {
    // null = not yet evaluated; otherwise an object describing channel access.
    const [underwritingTypes, setUnderwritingTypes] = useState(null);
    const [access, setAccess] = useState(null);
    const [toShowQuickPricer, setToShowQuickPricer] = useState(true);
    // VIEW_QUICKPRICER permission gates ONLY the correspondent channel.
    const [hasQuickPricerAccess, setHasQuickPricerAccess] = useState(null);

    useEffect(() => {
        const detectChannels = async () => {
            try {
                if (!window?.elli?.script?.getObject) {
                    setAccess({ wholesale: false, correspondent: false });
                    return;
                }

                window.elli.script.guest?.create?.();

                const authObject = await window.elli.script.getObject("auth");
                const authCode = await authObject.createAuthCode();
                const token = await getAccessToken(authCode);

                if (!token) {
                    setAccess({ wholesale: false, correspondent: false });
                    return;
                }

                const user = await authObject.getUser();
                const userDetails = await getUserDetails(token, user?.id);
                const orgId = userDetails?.organization?.entityId;

                if (!orgId) {
                    setAccess({ wholesale: false, correspondent: false });
                    return;
                }

                const tpoSettings = await getTpoSettingsAllBasicInfo(token, orgId);
                const channels = tpoSettings?.companyDetails?.channels ?? [];
                setUnderwritingTypes(tpoSettings?.companyDetails?.underwritingTypes ?? []);
                const reauthCode = await authObject.createAuthCode();
                const payload = {
                    orgId: orgId,
                    authCode: reauthCode
                };

                const response = await fetch(`${API_DOMAIN}/api/orgconfig/getorgconfig`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new Error(`HTTP ${response.status}: ${errorText}`);
                }

                const result = await response.json();
                setToShowQuickPricer(result == null ? true : result);
                // MOCK channels for local testing — remove before production.
                // channels.length = 0;
                // channels.push(WHOLESALE_CHANNEL);
                // channels.push(CORRESPONDENT_CHANNEL);
                console.log("Detected channels for Quick Pricer access:", channels);

                const hasChannel = (name) =>
                    Array.isArray(channels)
                        ? channels.includes(name)
                        : channels === name;

                // Only correspondent-only orgs need CREATE_LOANS persona check.
                const isCorrespondent = channels.includes("Correspondent");
                const isCorrespondentOnly = channels.includes("Correspondent") && !channels.includes("Broker");
                let hasQuickPricer = true;
                const personas = await getLoggedInUserPersonas();

                // apply restrictions for correspondent-only orgs based on personas/permissions
                if (isCorrespondent) {
                    try {
                        hasQuickPricer = hasPermission(personas, PERMISSIONS.VIEW_QUICKPRICER);
                    } catch (err) {
                        console.warn("Failed to evaluate VIEW_QUICKPRICER permission:", err);
                        hasQuickPricer = false;
                    }
                }

                if (isCorrespondentOnly) {
                    setHasQuickPricerAccess(hasQuickPricer);
                } else {
                    setHasQuickPricerAccess(true); // non-correspondent orgs have no restrictions  
                }

                setAccess({
                    wholesale: hasChannel(WHOLESALE_CHANNEL),
                    correspondent: hasChannel(CORRESPONDENT_CHANNEL) && hasQuickPricer,
                });
            } catch (err) {
                console.warn("Failed to detect channel access for Quick Pricer:", err);
                setAccess({ wholesale: false, correspondent: false });
                setHasQuickPricerAccess(false);
            }
        };

        detectChannels();
    }, []);

    if (hasQuickPricerAccess === null || access === null) {
        return <Loading />;
    }

    // Apply VIEW_QUICKPRICER gate to correspondent channel only.
    const effectiveAccess = {
        wholesale: access.wholesale && hasQuickPricerAccess === true,
        correspondent: access.correspondent && hasQuickPricerAccess === true && toShowQuickPricer === true,
    };

    const visibleSections = PRICER_SECTIONS.filter(
        (section) => effectiveAccess[section.key]
    ).map(item => ({
        ...item,
        links: item.links.filter(subItem => !subItem.key || underwritingTypes.includes(subItem.key))
    }));

    if (visibleSections.length === 0) {
        return (
            <div className="flex items-center justify-center p-6" style={{ minHeight: "60vh" }}>
                <div className="text-center text-gray-700 max-w-md">
                    <div className="text-lg font-semibold mb-2">Access Restricted</div>
                    <div className="text-sm">
                        The Quick Pricer is available only for wholesale or
                        correspondent channel users. If you believe this is a mistake,
                        please reach out to your System Administrator for access.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                width: "100%",
                fontFamily:
                    "ProximaNova-Regular, Helvetica, Arial, SansSerifFLF, sans-serif",
                color: "#23272f",
                marginTop: "40px",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "40px",
                    flexWrap: "wrap",
                    marginBottom: 32,
                }}
            >
                {visibleSections.map((section) => (
                    <PricerSection
                        key={section.key}
                        title={section.title}
                        links={section.links}
                    />
                ))}
            </div>
        </div>
    );
};

export default QuickPricer;